import {
  ForwardedRef,
  SelectHTMLAttributes,
  forwardRef,
  useState,
} from 'react';
import { Controller, FieldValues, RegisterOptions } from 'react-hook-form';
import {
  Variant,
  Variants,
} from '../../../page-builder/interface/checkoutInterface';
import {
  Country,
  ICountry,
  IProvince,
  State,
} from '../../../page-builder/interface/dataInterface';
import { TrackerConfig } from '../../interface/pixelInterface';
import { IDefaultOption } from '../../interface/customerInterface';
import { IBorderValueProps } from '../../puck/reusable-props/Border';
import {
  getBorderProps,
  getNewMarginStyles,
  getPaddingStyles,
} from '../../utils/helper';
import { PaddingType } from '../../puck/reusable-props/padding';
import { MarginType } from '../../puck/reusable-props/NewMargin';
import { IOutlineValueProps } from '../../puck/reusable-props/Outline';

interface ISelectProps extends SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  className?: string;
  options:
    | TrackerConfig[]
    | Country[]
    | ICountry[]
    | State[]
    | Variant[]
    | Variants[]
    | IProvince[]
    | { [key: string]: string }[]
    | IDefaultOption[];
  labelKey?: string;
  valueKey?: string;
  defaultOption?: string;
  placeHolderColor?: string;
  placeHolderFontSize?: string;
  placeHolderLineHeight?: string;
  placeHolderweight?: string;

  textColor?: {
    color: string;
    isInherit: boolean;
  };
  backgroundColor?: {
    color: string;
    isInherit: boolean;
  };
  PlaceHolderTextAlign?: string;
  placeHolderLetterSpace?: string;
  header?: {
    text: string;
    hide: boolean;
    padding: PaddingType;
    margin: MarginType;
  };

  placeHolderFontFamily?: string;
  fieldHeight?: string;
  error?: string;
  additionalStyles?: string;

  labelColor?: string;
  radiusBorder?: IBorderValueProps;
  outline?: IOutlineValueProps;
  currentDeviceType?: string;
}

export const Select = forwardRef(function TextFieldInternal(
  {
    label,
    className,
    options = [],
    labelKey = 'label',
    fieldHeight,
    valueKey = 'value',
    defaultOption = 'Select',
    error,
    additionalStyles = '',
    labelColor = 'text-gray-700',
    textColor,
    backgroundColor,
    placeHolderFontSize,
    placeHolderweight,
    placeHolderFontFamily,
    placeHolderLetterSpace,
    PlaceHolderTextAlign,
    placeHolderLineHeight,
    radiusBorder,
    outline,
    currentDeviceType,
    header,
    ...rest
  }: ISelectProps,
  ref: ForwardedRef<HTMLSelectElement>,
) {
  const [isInputFocused, setIsInputFocused] = useState(false);
  return (
    <div className={`${className}`}>
      {label && (
        <label
          htmlFor="email"
          className={`block text-sm font-medium pb-3 ${labelColor}`}>
          {label}
        </label>
      )}
      <div
        className="relative width-auto"
        onFocus={() => {
          setIsInputFocused(true);
        }}
        onBlur={() => {
          setIsInputFocused(false);
        }}>
        {header?.hide && (
          <div
            style={{
              ...getPaddingStyles(
                header?.padding,
                currentDeviceType || 'desktop',
              ),
              ...getNewMarginStyles(
                header.margin,
                currentDeviceType || 'desktop',
              ),
            }}
            className="text-sm mb-1 ml-2"
            dangerouslySetInnerHTML={{
              __html: header?.text,
            }}
          />
        )}
        <select
          aria-label="Country"
          ref={ref}
          style={{
            height: fieldHeight
              ? isNaN(+fieldHeight)
                ? fieldHeight
                : +fieldHeight
              : '48px',
            ...(isInputFocused
              ? outline
                ? getBorderProps(outline, currentDeviceType || 'desktop')
                : {}
              : radiusBorder
                ? getBorderProps(radiusBorder, currentDeviceType || 'desktop')
                : {}),
            fontFamily: placeHolderFontFamily,
            fontSize:
              placeHolderFontSize && placeHolderFontSize?.includes('%')
                ? placeHolderFontSize
                : `${placeHolderFontSize}px`,
            fontWeight: placeHolderweight,
            lineHeight: +(placeHolderLineHeight || 1.5),
            letterSpacing:
              placeHolderLetterSpace && placeHolderLetterSpace?.includes('%')
                ? placeHolderLetterSpace
                : `${placeHolderLetterSpace}px`,
            textAlign: PlaceHolderTextAlign as React.CSSProperties['textAlign'],
            color: textColor?.isInherit ? 'inherit' : textColor?.color,
            backgroundColor: backgroundColor?.isInherit
              ? 'inherit'
              : backgroundColor?.color,
          }}
          className={`block w-full  rounded-md border-gray-300 shadow-sm sm:text-sm active:ring-slate-300 focus:ring-slate-300 ring-slate-300 ${isInputFocused ? outline?.borderStyle?.[currentDeviceType || 'Desktop'] : radiusBorder?.borderStyle?.[currentDeviceType || 'desktop']} ${additionalStyles}`}
          {...rest}>
          <option value="" disabled>
            {defaultOption}
          </option>
          {options &&
            options.map((option, index) => (
              <option
                key={index}
                value={(option as { [key: string]: string })?.[valueKey]}>
                {(option as { [key: string]: string })?.[labelKey]}
              </option>
            ))}
        </select>
        {error && (
          <div
            className="error-message"
            style={{ color: 'var(--error-color)' }}>
            {error}
          </div>
        )}
      </div>
    </div>
  );
});

interface ISelectControlProps extends ISelectProps {
  name: string;
  rules?: Omit<
    RegisterOptions<FieldValues, string>,
    'disabled' | 'setValueAs' | 'valueAsNumber' | 'valueAsDate'
  >;
}
export const SelectControl = ({
  name,
  rules,
  radiusBorder,
  currentDeviceType,
  ...rest
}: ISelectControlProps) => {
  return (
    <Controller
      name={name}
      render={({
        field: { onChange, ...restField },
        fieldState: { error },
      }) => {
        return (
          <Select
            {...rest}
            onChange={e => onChange(e.target.value)}
            {...restField}
            error={error?.message}
            radiusBorder={radiusBorder}
            currentDeviceType={currentDeviceType}
          />
        );
      }}
      rules={rules}
    />
  );
};
